import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useOutletContext } from 'react-router-dom'
import Swal from 'sweetalert2'
import {
  genWin2KeepOrderNoDouble,
  genWin2KeepOrderIncludeDouble,
  genWin3KeepOrderNoHamNoTong,
  genWin3KeepOrderIncludeHamTong,
} from '../../utils/winNumberUtils'
import { numberSetService, type NumberSetItem } from '../../services/numberSetService'
import { CUSTOMER_SLIP_CANCEL_WINDOW_MINUTES, formatCustomerSlipCancelWindowText, lotteryService } from '../../services/lotteryService'
import RulesGuideModal from '../../components/RulesGuideModal'
import type { LobbyHuayOutletContext } from './ModernLobbyHuayLayout'
import './ModernLobbyHuayForms.css'

/** Map typeLabel (จับวิน) → prizeTypeCode (API เลขชุด) — ตรงกับ user-classic NumberSetCreatePage */
const TYPE_LABEL_TO_PRIZE_CODE: Record<string, string> = {
  '2 ตัวบน': '2_DIGIT_TOP',
  '3 ตัวบน': '3_DIGIT_TOP',
  '3 ตัวโต๊ด': '3_DIGIT_TOD',
  '2 ตัวโต๊ด': '2_DIGIT_TOD',
  '2 ตัวล่าง': '2_DIGIT_BOTTOM',
  '3 ตัวล่าง': '3_DIGIT_BOTTOM',
}

type WinResultItem = { id: string; type: string; number: string }

/** Payload ส่งไปหน้า bet เพื่อนำเลขวินไปใส่ตารางแทง */
export interface FromWinNumberItem {
  type: string
  number: string
}

/** หน้า จับวินเลข — ยึดลำดับตัวเลขที่เลือก (ไม่ sort) */
const ModernLobbyHuayWinNumberPage: React.FC = () => {
  useOutletContext<LobbyHuayOutletContext>()
  const navigate = useNavigate()

  const [setName, setSetName] = useState('')
  const setNameInputRef = useRef<HTMLInputElement>(null)
  const [selectedDigits, setSelectedDigits] = useState<number[]>([])
  const [results, setResults] = useState<WinResultItem[]>([])
  const [rulesModalOpen, setRulesModalOpen] = useState(false)
  const [cancelWindowMinutes, setCancelWindowMinutes] = useState(CUSTOMER_SLIP_CANCEL_WINDOW_MINUTES)

  useEffect(() => {
    let isMounted = true
    void lotteryService.getCustomerSlipCancelWindowMinutes().then((minutes) => {
      if (isMounted) setCancelWindowMinutes(minutes)
    })
    return () => {
      isMounted = false
    }
  }, [])
  const [saving, setSaving] = useState(false)
  /** จับวิน 2 ตัว: เลือกได้อย่างใดอย่างหนึ่ง (ไม่รวมเบิ้ล หรือ รวมเบิ้ล) */
  const [selectedWin2, setSelectedWin2] = useState<'no-double' | 'with-double' | null>(null)
  /** จับวิน 3 ตัว: เลือกได้อย่างใดอย่างหนึ่ง (ไม่รวมหามตอง หรือ รวมหามตอง) */
  const [selectedWin3, setSelectedWin3] = useState<'no-triple' | 'with-triple' | null>(null)

  const digits = [1, 2, 3, 4, 5, 6, 7, 8, 9, 0]

  const toggleDigit = useCallback((d: number) => {
    setSelectedDigits((prev) => {
      const i = prev.indexOf(d)
      if (i >= 0) return prev.filter((_, idx) => idx !== i)
      return [...prev, d]
    })
  }, [])

  const selectedDisplay = useMemo(
    () => selectedDigits.join(','),
    [selectedDigits]
  )

  const canGenerate = selectedDigits.length >= 3

  const addResults = useCallback((typeLabel: string, numbers: string[]) => {
    const newItems: WinResultItem[] = numbers.map((number, i) => ({
      id: `win-${Date.now()}-${i}-${number}`,
      type: typeLabel,
      number,
    }))
    setResults((prev) => [...prev, ...newItems])
  }, [])

  /** ปุ่ม 4 อัน: เลือก/ยกเลิกเท่านั้น (กดซ้ำ = ลบออก) — ยังไม่สร้างเลขจนกว่ากด "จับวินตัวเลข" */
  const handleWin2NoDouble = useCallback(() => {
    if (!canGenerate) return
    setSelectedWin2((prev) => (prev === 'no-double' ? null : 'no-double'))
  }, [canGenerate])

  const handleWin2WithDouble = useCallback(() => {
    if (!canGenerate) return
    setSelectedWin2((prev) => (prev === 'with-double' ? null : 'with-double'))
  }, [canGenerate])

  const handleWin3NoTriple = useCallback(() => {
    if (!canGenerate) return
    setSelectedWin3((prev) => (prev === 'no-triple' ? null : 'no-triple'))
  }, [canGenerate])

  const handleWin3WithTriple = useCallback(() => {
    if (!canGenerate) return
    setSelectedWin3((prev) => (prev === 'with-triple' ? null : 'with-triple'))
  }, [canGenerate])

  /** ลำดับแสดงประเภทในรายการทั้งหมด (ครบ 6 ประเภท) */
  const PRIZE_TYPE_ORDER = [
    '2 ตัวบน',
    '3 ตัวบน',
    '3 ตัวโต๊ด',
    '2 ตัวโต๊ด',
    '2 ตัวล่าง',
    '3 ตัวล่าง',
  ] as const

  /** กด "จับวินตัวเลข" ถึงจะสร้างเลขวิน — ผลลัพธ์กระจายเข้า 6 ประเภท (2 ตัวบน, 3 ตัวบน, 3 ตัวโต๊ด, 2 ตัวโต๊ด, 2 ตัวล่าง, 3 ตัวล่าง) */
  const hasWinSelection = selectedWin2 != null || selectedWin3 != null
  const handleDrawWinNumbers = useCallback(() => {
    if (!canGenerate || !hasWinSelection) return
    try {
      if (selectedWin2 === 'no-double') {
        const numbers = genWin2KeepOrderNoDouble(selectedDigits)
        addResults('2 ตัวบน', numbers)
        addResults('2 ตัวโต๊ด', numbers)
        addResults('2 ตัวล่าง', numbers)
      }
      if (selectedWin2 === 'with-double') {
        const numbers = genWin2KeepOrderIncludeDouble(selectedDigits)
        addResults('2 ตัวบน', numbers)
        addResults('2 ตัวโต๊ด', numbers)
        addResults('2 ตัวล่าง', numbers)
      }
      if (selectedWin3 === 'no-triple') {
        const numbers = genWin3KeepOrderNoHamNoTong(selectedDigits)
        addResults('3 ตัวบน', numbers)
        addResults('3 ตัวโต๊ด', numbers)
        addResults('3 ตัวล่าง', numbers)
      }
      if (selectedWin3 === 'with-triple') {
        const numbers = genWin3KeepOrderIncludeHamTong(selectedDigits)
        addResults('3 ตัวบน', numbers)
        addResults('3 ตัวโต๊ด', numbers)
        addResults('3 ตัวล่าง', numbers)
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'เกิดข้อผิดพลาด'
      window.alert(msg)
    }
  }, [canGenerate, hasWinSelection, selectedWin2, selectedWin3, selectedDigits, addResults])

  const removeResult = useCallback((id: string) => {
    setResults((prev) => prev.filter((r) => r.id !== id))
  }, [])

  /** จัดกลุ่มตามประเภท และเรียงตาม PRIZE_TYPE_ORDER (แสดงครบ 6 ประเภท) */
  const groupByType = useMemo(() => {
    const map = new Map<string, WinResultItem[]>()
    results.forEach((r) => {
      const list = map.get(r.type) ?? []
      list.push(r)
      map.set(r.type, list)
    })
    return PRIZE_TYPE_ORDER.map((typeLabel) => ({
      typeLabel,
      items: map.get(typeLabel) ?? [],
    }))
  }, [results])

  const deleteByType = useCallback((typeLabel: string) => {
    setResults((prev) => prev.filter((r) => r.type !== typeLabel))
  }, [])

  /** ล้างทั้งหมดเหมือนเข้าใหม่: ตาราง, เลขที่เลือก, จับวิน 2/3, ชื่อชุด */
  const clearAll = useCallback(() => {
    setResults([])
    setSelectedDigits([])
    setSelectedWin2(null)
    setSelectedWin3(null)
    setSetName('')
  }, [])

  /** บันทึกข้อมูล: บังคับชื่อชุด + มีรายการจับวิน แล้วเรียก API สร้างเลขชุด (เดียวกับ user-classic) */
  const handleSaveData = useCallback(async () => {
    const name = setName.trim()
    if (!name) {
      Swal.fire({
        toast: true,
        position: 'top',
        icon: 'warning',
        title: 'กรุณาระบุชื่อเลขชุด',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      })
      setTimeout(() => setNameInputRef.current?.focus(), 100)
      return
    }
    if (results.length === 0) {
      Swal.fire({
        toast: true,
        position: 'top',
        icon: 'warning',
        title: 'กรุณาจับวินตัวเลขก่อน',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      })
      return
    }
    const items: NumberSetItem[] = groupByType
      .filter((g) => g.items.length > 0)
      .map((g) => ({
        prizeTypeCode: TYPE_LABEL_TO_PRIZE_CODE[g.typeLabel] ?? g.typeLabel,
        typeLabel: g.typeLabel,
        numbers: g.items.map((i) => i.number).join(', '),
      }))
    if (items.length === 0) return
    try {
      setSaving(true)
      await numberSetService.create({ name, items })
      Swal.fire({
        toast: true,
        position: 'top',
        icon: 'success',
        title: 'บันทึกเลขชุดเรียบร้อยแล้ว',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      })
      clearAll()
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'บันทึกเลขชุดไม่สำเร็จ'
      Swal.fire({
        toast: true,
        position: 'top',
        icon: 'error',
        title: msg,
        showConfirmButton: false,
        timer: 4000,
        timerProgressBar: true,
      })
    } finally {
      setSaving(false)
    }
  }, [setName, results.length, groupByType, clearAll])

  return (
    <div className="htd-win">
      <div className="lobby-huay-page-header">
        <Link to="/lobby-huay/set" className="lobby-huay-page-header__back">
          <i className="fas fa-chevron-left" aria-hidden /> กลับ
        </Link>
        <h3 className="lobby-huay-page-header__title">จับวินเลข</h3>
        <div className="htd-win__header-extra">
          <button type="button" className="htd-win__rules-btn" onClick={() => setRulesModalOpen(true)}>
            กติกา & วิธีดูผล
          </button>
        </div>
      </div>

      <div className="htd-win__grid">
        <div className="htd-form" style={{ gap: '0.7rem' }}>
          <h4 className="htd-win__section-title">
            <i className="fas fa-list" aria-hidden />
            เลขชุด
          </h4>

          <div className="htd-form__panel">
            <label className="htd-form__label" htmlFor="htd-win-set-name">
              ระบุชื่อเรียกเลขชุด
            </label>
            <div className="htd-form__field">
              <i className="fas fa-keyboard" aria-hidden />
              <input
                id="htd-win-set-name"
                ref={setNameInputRef}
                type="text"
                placeholder="ชื่อเลขชุด"
                value={setName}
                onChange={(e) => setSetName(e.target.value)}
              />
            </div>
          </div>

          <div className="htd-win__picked">
            <small>รายการที่เลือก</small>
            <strong>{selectedDisplay || '-'}</strong>
          </div>

          <div className="htd-form__panel">
            <p className="htd-win__hint">เลือกตัวเลขอย่างน้อย 3 ตัวขึ้นไป</p>
            <div className="htd-win__digits" style={{ marginTop: '0.55rem' }}>
              {digits.map((d) => {
                const isActive = selectedDigits.includes(d)
                return (
                  <button
                    key={d}
                    type="button"
                    className={`htd-win__digit${isActive ? ' is-active' : ''}`}
                    aria-pressed={isActive}
                    onClick={() => toggleDigit(d)}
                  >
                    {d}
                  </button>
                )
              })}
            </div>

            <div className="htd-win__opts" style={{ marginTop: '0.75rem' }}>
              <button
                type="button"
                className={`htd-win__opt${selectedWin2 === 'no-double' ? ' is-active' : ''}`}
                onClick={handleWin2NoDouble}
              >
                จับวิน 2 ตัวไม่รวมเลขเบิ้ล
              </button>
              <button
                type="button"
                className={`htd-win__opt${selectedWin2 === 'with-double' ? ' is-active' : ''}`}
                onClick={handleWin2WithDouble}
              >
                จับวิน 2 ตัวรวมเลขเบิ้ล
              </button>
              <button
                type="button"
                className={`htd-win__opt${selectedWin3 === 'no-triple' ? ' is-active' : ''}`}
                onClick={handleWin3NoTriple}
              >
                จับวิน 3 ตัวไม่รวมเลขหามตอง
              </button>
              <button
                type="button"
                className={`htd-win__opt${selectedWin3 === 'with-triple' ? ' is-active' : ''}`}
                onClick={handleWin3WithTriple}
              >
                จับวิน 3 ตัวรวมเลขหามตอง
              </button>
            </div>
          </div>

          <button
            type="button"
            className="htd-form__btn htd-form__btn--primary"
            disabled={!canGenerate || !hasWinSelection}
            onClick={handleDrawWinNumbers}
          >
            <i className="fas fa-random" aria-hidden />
            จับวินตัวเลข
          </button>
        </div>

        <div className="htd-form" style={{ gap: '0.7rem' }}>
          <h4 className="htd-win__section-title">
            <i className="fas fa-table" aria-hidden />
            รายการทั้งหมด
          </h4>

          {results.length === 0 ? (
            <div className="htd-slip__state">
              <p>ยังไม่มีรายการจับวิน — เลือกตัวเลขแล้วกดปุ่มจับวินด้านซ้าย</p>
            </div>
          ) : (
            groupByType.map(({ typeLabel, items }) => (
              <div key={typeLabel} className="htd-win__type-block">
                <div className="htd-win__type-head">
                  <strong>{typeLabel}</strong>
                  <button type="button" disabled={items.length === 0} onClick={() => deleteByType(typeLabel)}>
                    <i className="fas fa-trash-alt me-1" aria-hidden />
                    ลบชนิดนี้
                  </button>
                </div>
                <div className="htd-form__table-wrap" style={{ border: 0, borderRadius: 0 }}>
                  <table className="htd-form__table">
                    <thead>
                      <tr>
                        <th>##</th>
                        <th>ชนิด</th>
                        <th>ตัวเลข</th>
                        <th>ลบ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.length === 0 ? (
                        <tr>
                          <td colSpan={4}>
                            <p className="htd-form__empty">ไม่มีรายการ</p>
                          </td>
                        </tr>
                      ) : (
                        items.map((row, idx) => (
                          <tr key={row.id}>
                            <td>{idx + 1}</td>
                            <td>
                              <span className="htd-form__type-chip">{row.type}</span>
                            </td>
                            <td>
                              <strong>{row.number}</strong>
                            </td>
                            <td>
                              <button
                                type="button"
                                className="htd-form__icon-btn"
                                aria-label={`ลบ ${row.number}`}
                                onClick={() => removeResult(row.id)}
                              >
                                <i className="fas fa-trash-alt" />
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            ))
          )}

          <div className="htd-form__actions" style={{ gridTemplateColumns: '1fr' }}>
            <Link to="/lobby-huay/set" className="htd-form__btn htd-form__btn--secondary">
              <i className="fas fa-plus-square" aria-hidden />
              ไปหน้าเลขชุด
            </Link>
            <button type="button" className="htd-form__btn htd-form__btn--secondary" onClick={clearAll}>
              <i className="fas fa-minus-square" aria-hidden />
              ล้างเลขจับวินทั้งหมด
            </button>
            <button
              type="button"
              className="htd-form__btn htd-form__btn--primary"
              disabled={results.length === 0}
              onClick={() => {
                const payload: FromWinNumberItem[] = results.map((r) => ({ type: r.type, number: r.number }))
                navigate('/lobby-huay/listall', { state: { fromWinNumber: payload } })
              }}
            >
              <i className="fas fa-pencil-alt" aria-hidden />
              แทงหวยเลย
            </button>
            <div className="htd-form__actions">
              <button
                type="button"
                className="htd-form__btn htd-form__btn--primary"
                disabled={saving || results.length === 0}
                onClick={() => void handleSaveData()}
              >
                {saving ? <i className="fas fa-spinner fa-spin" aria-hidden /> : <i className="fas fa-check-circle" aria-hidden />}
                {saving ? 'กำลังบันทึก...' : 'บันทึกข้อมูล'}
              </button>
              <Link to="/lobby-huay/set" className="htd-form__btn htd-form__btn--danger">
                <i className="fas fa-times-circle" aria-hidden />
                ยกเลิก
              </Link>
            </div>
          </div>
        </div>
      </div>

      <RulesGuideModal
        isOpen={rulesModalOpen}
        onClose={() => setRulesModalOpen(false)}
        fallbackRules={(
          <>
            <p>- ระบบเปิด-ปิดแทงตามเวลาที่ได้แจ้งไว้ และบริษัทสามารถพิจารณาปิดแทงก่อนกำหนดได้</p>
            <p>- ทางบริษัทสามารถคืนเลขได้ทุกกรณี แต่จะคืนก่อนที่จะออกผลรางวัล</p>
            <p>- การคืนเลขหรือคืนโพยของสมาชิก ต้องเป็นไปเงื่อนไขและเวลาที่ทางเว็บกำหนดเท่านั้น (<span className="c_primary"><strong>คืนโพยได้ภายใน {formatCustomerSlipCancelWindowText(cancelWindowMinutes)}หลังจากที่แทง</strong></span>)</p>
            <p>- หากหวยนั้นๆ มีการออกผลรางวัลก่อนที่จะปิดแทง บริษัทจะทำการยกเลิกรายการที่แทงเฉพาะบางเลขที่ได้ทำการแทงหลังจากหวยนั้นๆ ออกผล</p>
            <p>- ราคาจ่ายและส่วนลดจะลดลงตามเลขดังหรือเลขอั้น ให้สมาชิกตรวจสอบโพยและราคาจ่าย/ส่วนลดหลังการแทงทุกครั้ง</p>
            <p>- การสมัครสมาชิกหรือแนะนำชื่อตัวเองเพื่อปั่นค่าคอมมิชชั่น จะถูกพิจารณาปิดบัญชีและยึดเครดิตของบัญชีที่เกี่ยข้องทั้งหมด</p>
            <p>- สมาชิกที่จงใจเจตนาทุจริตในการเล่น จะถูกปิดบัญชีและไม่คืนเครดิตให้ทุกกรณี</p>
            <p>- กรณีที่เซิฟเวอร์หรืออินเตอร์เน็ตล่มในช่วงการเปิดรับแทง และเกิดข้อโต้แย้งเกี่ยวกับการออกผลรางวัล ทางบริษัทมีสิทธิ์จะพิจารณายกเลิกรายการงวดและคืนเครดิตให้ลูกค้า</p>
            <p>- ผลรางวัลคลุมเครือหรือมีแนวโน้มที่ไม่โปร่งใส ทางบริษัทจะพิจารณายกเลิกรายการงวดและคืนเครดิตให้ลูกค้า</p>
            <p>- ในกรณีเกิดข้อพิพาทและโต้แย้งในการออกผลรางวัล การตัดสินของบริษัทถือเป็นข้อยุติและสิ้นสุด</p>
            <p>- <span className="c_primary">สมาชิกที่สมัครมากกว่า 1 ยูสเซอร์ โดยใช้ชื่อเดียวกัน เพื่อผลประโยชน์ในการแทงได้มากกว่าหรือปั่นค่าแนะนำ จะถูกระงับและไม่คืนเครดิตให้ทุกรณี</span></p>
          </>
        )}
        fallbackGuides={(
          <>
            <p className="mt-3"><u><strong>วิธีการดูผลรางวัล &apos;หวยรัฐบาลไทย&apos;</strong></u></p>
            <div className="mt-3 mb-4 text-center">
              <p className="mb-1">ใช้รางวัลที่ 1 ในการออกผลรางวัล เช่น 199606</p>
              <p className="mb-1">สามตัวบน (606) สองตัวล่าง (94)</p>
              <p className="mb-1">สามตัวล่าง (220,173,388,094)</p>
            </div>
            <p><u><strong>การตรวจผลรางวัล</strong></u></p>
            <table className="table table-bordered mb-0">
              <tbody>
                <tr><td className="text-nowrap" style={{ width: 100 }}>3 ตัวบน</td><td>606</td></tr>
                <tr><td className="text-nowrap">2 ตัวบน</td><td>06</td></tr>
                <tr><td className="text-nowrap">2 ตัวโต๊ด</td><td>94 49 06 60</td></tr>
                <tr><td className="text-nowrap">3 ตัวโต๊ด</td><td>606 660 066</td></tr>
                <tr><td className="text-nowrap">วิ่งบน</td><td>6 - 0 - 6</td></tr>
                <tr><td className="text-nowrap">ปักหลักร้อย</td><td>6</td></tr>
                <tr><td className="text-nowrap">ปักหลักสิบ</td><td>0</td></tr>
                <tr><td className="text-nowrap">ปักหลักหน่วย</td><td>6</td></tr>
                <tr><td className="text-nowrap">2 ตัวล่าง</td><td>94</td></tr>
                <tr><td className="text-nowrap">วิ่งล่าง</td><td>4 - 9</td></tr>
                <tr><td className="text-nowrap">4 ตัวบน</td><td>9606</td></tr>
                <tr><td className="text-nowrap">4 ตัวโต๊ด</td><td>9606 สลับตำแหน่งทั้ง 4 หลัก</td></tr>
                <tr><td className="text-nowrap">5 ตัวโต๊ด</td><td>99606 สลับตำแหน่งทั้ง 5 หลัก</td></tr>
              </tbody>
            </table>
          </>
        )}
      />
    </div>
  )
}

export default ModernLobbyHuayWinNumberPage
